import type React from "react";
import type { IconType } from "react-icons";
import {
  FiAlertCircle,
  FiBriefcase,
  FiCheckSquare,
  FiClock,
  FiCompass,
  FiFolder,
  FiHome,
  FiKey,
  FiLayers,
  FiList,
  FiShare2,
  FiSmartphone,
  FiTool,
  FiUserMinus,
  FiUserPlus,
} from "react-icons/fi";
import type { AdminGuideSectionId } from "./primitives";
import { AdminPages, HowAccessWorks, StartHere } from "./sections/basics";
import {
  OnboardingAnAsset,
  OnboardingAssetGroup,
  OnboardingClient,
  OnboardingCompany,
  OnboardingUser,
} from "./sections/onboarding";
import {
  FileCenterFolders,
  MethodAccess,
  MobileUsers,
  Offboarding,
  OutageAssets,
  WhenChangesApply,
} from "./sections/managing";
import { Troubleshooting } from "./sections/help";

export type { AdminGuideSectionId } from "./primitives";

export type AdminGuideGroupId = "basics" | "onboarding" | "managing" | "help";

export const ADMIN_GUIDE_GROUPS: { id: AdminGuideGroupId; label: string }[] = [
  { id: "basics", label: "Basics" },
  { id: "onboarding", label: "Onboarding" },
  { id: "managing", label: "Managing access" },
  { id: "help", label: "Help" },
];

export interface AdminGuideSection {
  id: AdminGuideSectionId;
  group: AdminGuideGroupId;
  label: string;
  description: string;
  icon: IconType;
  body: React.FC;
}

/** Every section, in reading order. The nav groups them by `group`. */
export const ADMIN_GUIDE_SECTIONS: AdminGuideSection[] = [
  { id: "start-here", group: "basics", label: "Start here", description: "Who this is for and the common jobs", icon: FiCompass, body: StartHere },
  { id: "how-access-works", group: "basics", label: "How access works", description: "Users, roles, groups and objects", icon: FiShare2, body: HowAccessWorks },
  { id: "admin-pages", group: "basics", label: "Admin pages", description: "What each tab is for", icon: FiList, body: AdminPages },

  { id: "onboarding-client", group: "onboarding", label: "Onboarding a client", description: "Company, asset group, users and role", icon: FiBriefcase, body: OnboardingClient },
  { id: "onboarding-company", group: "onboarding", label: "Onboarding a company", description: "The company and its email domains", icon: FiHome, body: OnboardingCompany },
  { id: "onboarding-asset-group", group: "onboarding", label: "Onboarding an asset group", description: "Which assets a role can see", icon: FiLayers, body: OnboardingAssetGroup },
  { id: "onboarding-user", group: "onboarding", label: "Onboarding a user", description: "Invite a person, pick their roles", icon: FiUserPlus, body: OnboardingUser },
  { id: "onboarding-an-asset", group: "onboarding", label: "Onboarding an asset", description: "New asset, Fix all and the checklist", icon: FiCheckSquare, body: OnboardingAnAsset },

  { id: "method-access", group: "managing", label: "Method access", description: "Levels per HTTP method, stored paths", icon: FiKey, body: MethodAccess },
  { id: "filecenter-folders", group: "managing", label: "FileCenter folders", description: "Folder objects and their groups", icon: FiFolder, body: FileCenterFolders },
  { id: "outage-assets", group: "managing", label: "Outage assets", description: "Who can pick which asset", icon: FiTool, body: OutageAssets },
  { id: "mobile-users", group: "managing", label: "Mobile users", description: "Mobile roles and asset groups", icon: FiSmartphone, body: MobileUsers },
  { id: "when-changes-apply", group: "managing", label: "When changes apply", description: "Cache and refresh rules", icon: FiClock, body: WhenChangesApply },
  { id: "offboarding", group: "managing", label: "Offboarding", description: "Disabling users, inactive users", icon: FiUserMinus, body: Offboarding },

  { id: "troubleshooting", group: "help", label: "Troubleshooting", description: "Symptoms, causes and fixes", icon: FiAlertCircle, body: Troubleshooting },
];
